import type { CompanyOrderRepository } from "../../../domain/repositories/company/company-order-repository";
import { companyOrderApiService } from "../../../infrastructure/api/company/company-order-api-service";
import {
    type GetOrdersResponse,
    GetOrderResponseSchema,
    type CreateOrderRequest,
    type CreateOrderResponse,
    type Courier,
    type OrderPageResponse,
    type BulkShipmentRegisterResponse,
    type OrderSearchSuggestion,
} from "../../../domain/types/orders";
import type { ApiResponse } from "../../../shared/types/common";
import { type GetResubmissionResponse, GetResubmissionResponseSchema } from "../../../domain/types/resubmission";

export class CompanyOrderRepositoryImpl implements CompanyOrderRepository {
    async trackingOrder(companyId: number, orderId: number, trackingNumber: string, courier: string): Promise<ApiResponse> {
        const api = await companyOrderApiService.trackingOrder(companyId, orderId, trackingNumber, courier);

        if (api.message) {
            throw new Error(api.message || "배송추적에 실패했습니다.");
        }

        return api;
    }
    async refreshTracking(companyId: number, orderId: number): Promise<ApiResponse> {
        const api = await companyOrderApiService.refreshTracking(companyId, orderId);

        if (api.message) {
            throw new Error(api.message || "배송 정보를 갱신하지 못했습니다.");
        }

        return api;
    }
    async updateTrackingInfo(companyId: number, orderId: number, courier: string, trackingNumber: string): Promise<ApiResponse> {
        const api = await companyOrderApiService.updateTrackingInfo(companyId, orderId, courier, trackingNumber);

        if (api.message) {
            throw new Error(api.message || "재접수 변경에 실패했습니다.");
        }

        return api;
    }
    
    async getCourierList(): Promise<Courier[]> {
        return await companyOrderApiService.getCourierList();
    }
    
    getOrderSearchSuggestions(companyId: number, keyword: string): Promise<OrderSearchSuggestion[]> {
        return companyOrderApiService.getOrderSearchSuggestions(companyId, keyword);
    }

    async getOrders(companyId: number, status?: string, keyword?: string, page?: number, size?: number, dateFrom?: string, dateTo?: string): Promise<OrderPageResponse> {
        const response = await companyOrderApiService.getOrders(companyId, status, keyword, page, size, dateFrom, dateTo);

        if (!response.data) {
            throw new Error(response.errorMsg || "주문을 불러오지 못했습니다.");
        }

        // API 응답이 배열인 경우와 PageResponse 객체인 경우 모두 처리
        const isArrayResponse = Array.isArray(response.data);
        const items = isArrayResponse ? response.data : (response.data.items ?? []);

        // 페이징 응답에서 items 배열 검증
        const itemsParsed = GetOrderResponseSchema.safeParse(items);
        if (!itemsParsed.success) {
            console.error("[getOrders] Schema validation failed:", itemsParsed.error.issues);
            console.error("[getOrders] Response data:", response.data);
            const issues = itemsParsed.error.issues.map(i => `${i.path.join(".")}: ${i.message}`).join("; ");
            throw new Error(`GetOrderResponse schema validation failed: ${issues}`);
        }

        // 배열 응답인 경우 페이징 정보 없음
        if (isArrayResponse) {
            return {
                items: itemsParsed.data,
                totalPages: 1,
                totalElements: itemsParsed.data.length,
                pageSize: itemsParsed.data.length,
                next: null,
                previous: null,
            };
        }

        return {
            items: itemsParsed.data,
            totalPages: response.data.totalPages ?? 0,
            totalElements: response.data.totalElements ?? 0,
            pageSize: response.data.pageSize ?? 30,
            next: response.data.next,
            previous: response.data.previous,
        };
    }

    async resubmission(companyId: number, orderId: number, memo: string): Promise<ApiResponse> {
        const api = await companyOrderApiService.resubmission(companyId, orderId, memo);

        if (api.message) {
            throw new Error(api.message || "재접수 변경에 실패했습니다.");
        }

        return api;
    }

    async resubmissionList(companyId: number): Promise<GetResubmissionResponse> {
        const response = await companyOrderApiService.resubmissionList(companyId);

        if (!response.data) {
            throw new Error(response.errorMsg || "주문을 불러오지 못했습니다.");
        }

        console.log(response.data)

        const pared = GetResubmissionResponseSchema.safeParse(response.data);
        if (!pared.success) {
            const issues = pared.error.issues.map(i => `${i.path.join(".")}: ${i.message}`).join("; ");
            throw new Error(`GetOrderResponse schema validation failed: ${issues}`);
        }

        return pared.data;
    }

    async acceptOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        const api = await companyOrderApiService.acceptOrder(companyId, orderId);

        if (api.message) {
            throw new Error(api.message || "접수 변경에 실패했습니다.");
        }

        return api;
    }

    async startProduction(companyId: number, orderId: number): Promise<ApiResponse> {
        const api = await companyOrderApiService.startProduction(companyId, orderId);

        if (api.message) {
            throw new Error(api.message || "제작 시작에 실패했습니다.");
        }

        return api;
    }

    async completeProduction(companyId: number, orderId: number): Promise<ApiResponse> {
        const api = await companyOrderApiService.completeProduction(companyId, orderId);

        if (api.message) {
            throw new Error(api.message || "제작 완료 처리에 실패했습니다.");
        }

        return api;
    }

    async completeDelivery(companyId: number, orderId: number): Promise<ApiResponse> {
        const api = await companyOrderApiService.completeDelivery(companyId, orderId);

        if (api.message) {
            throw new Error(api.message || "배송 완료 처리에 실패했습니다.");
        }

        return api;
    }

    async shipOrder(companyId: number, orderId: number, courier?: string, trackingNumber?: string): Promise<ApiResponse> {
        const api = await companyOrderApiService.shipOrder(companyId, orderId, courier, trackingNumber);

        if (api.message) {
            throw new Error(api.message || "출고/배송 처리에 실패했습니다.");
        }

        return api;
    }

    async cancelOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        const api = await companyOrderApiService.cancelOrder(companyId, orderId);

        if (api.message) {
            throw new Error(api.message || "주문 취소에 실패했습니다.");
        }

        return api;
    }

    async refundOrder(companyId: number, orderId: number): Promise<ApiResponse> {
        const api = await companyOrderApiService.refundOrder(companyId, orderId);

        if (api.message) {
            throw new Error(api.message || "환불 처리에 실패했습니다.");
        }

        return api;
    }

    async resetToBeforePayment(companyId: number, orderId: number): Promise<ApiResponse> {
        const api = await companyOrderApiService.resetToBeforePayment(companyId, orderId);

        if (api.message) {
            throw new Error(api.message || "결제전 상태 변경에 실패했습니다.");
        }

        return api;
    }

    async createOrder(companyId: number, orderData: CreateOrderRequest, orderSourceFile?: File): Promise<CreateOrderResponse> {
        return await companyOrderApiService.createOrder(companyId, orderData, orderSourceFile);
    }

    async bulkShipmentRegister(companyId: number, file: File): Promise<BulkShipmentRegisterResponse> {
        return await companyOrderApiService.bulkShipmentRegister(companyId, file);
    }

    async downloadShippingTemplate(companyId: number): Promise<Blob> {
        return await companyOrderApiService.downloadShippingTemplate(companyId);
    }
}
